import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Text,
  Stack,
  LoadingOverlay,
  Alert,
  Box,
  Badge,
  Card,
  Group,
  Progress,
  Button,
  Skeleton,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconCheck,
  IconUsers,
  IconUserEdit,
} from "@tabler/icons-react";
import { useApp } from "../context/AppContext";
import { useSalon } from "../context/SalonContext";
import { useResponsive } from "../context/ResponsiveContext";
import { useFeatureAccess } from "../context/FeatureAccessContext";
import { useCheckoutStatus } from "../hooks/useCheckoutStatus";
import {
  SUBSCRIPTION_TIERS,
  SubscriptionTier,
  SubscriptionTierType,
  GetCheckoutUrlRequest,
  GetCheckoutUrlResponse,
  SubscriptionTierPricing,
} from "../types/subscription";
import { BusinessInfo, CurrentUser } from "../types/context";
import { TIER_CONFIG } from "../types/featureAccess";
import styles from "./SubscriptionPricing.module.css";

interface SubscriptionPricingProps {
  onSuccess?: () => void;
}

function SubscriptionPricing({ onSuccess }: SubscriptionPricingProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingTier, setLoadingTier] = useState<SubscriptionTierType | null>(
    null,
  );
  const { message, messageType, setMessage, setMessageType } =
    useCheckoutStatus(onSuccess);
  const { getCheckoutUrl, getSubscriptionPricing } = useApp();
  const [livePricing, setLivePricing] = useState<
    Record<string, SubscriptionTierPricing>
  >({});
  // Tracks tiers whose live-price fetch failed so we can fall back to the
  // hardcoded `tier.price` instead of stranding the user on a skeleton.
  const [livePricingErrors, setLivePricingErrors] = useState<
    Record<string, true>
  >({});
  const navigate = useNavigate();
  const { currentUser, businessInfo } = useSalon() as {
    currentUser: CurrentUser | null;
    businessInfo: BusinessInfo | null;
  };
  const { isMobile } = useResponsive();
  const { access, getTierDisplayName, businessVertical } = useFeatureAccess();

  // Show only tiers available to this salon's business vertical. Tiers with
  // `requiredBusinessVertical` are MedSpa-only (or other vertical-only); the
  // standard three tiers stay hidden from those salons since their pricing is
  // managed separately.
  const visibleTiers = useMemo<SubscriptionTier[]>(() => {
    const verticalRestricted = SUBSCRIPTION_TIERS.filter(
      (t) => t.requiredBusinessVertical,
    );
    const matchingRestricted = verticalRestricted.find(
      (t) => t.requiredBusinessVertical === businessVertical,
    );
    if (matchingRestricted) {
      return [matchingRestricted];
    }
    return SUBSCRIPTION_TIERS.filter((t) => !t.requiredBusinessVertical);
  }, [businessVertical]);

  // Fetch live Stripe prices for any visible tier that opts into live pricing.
  useEffect(() => {
    const tiersNeedingLivePrice = visibleTiers.filter((t) => t.fetchLivePrice);
    if (tiersNeedingLivePrice.length === 0) return;
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        tiersNeedingLivePrice.map(async (t) => {
          try {
            const pricing = await getSubscriptionPricing(t.id);
            return { tierId: t.id, pricing, error: false as const };
          } catch (err) {
            console.error("Failed to fetch live pricing for tier", t.id, err);
            return { tierId: t.id, pricing: null, error: true as const };
          }
        }),
      );
      if (cancelled) return;
      const nextPricing: Record<string, SubscriptionTierPricing> = {};
      const nextErrors: Record<string, true> = {};
      for (const r of results) {
        if (r.error) {
          nextErrors[r.tierId] = true;
        } else if (r.pricing) {
          nextPricing[r.tierId] = r.pricing;
        }
      }
      setLivePricing(nextPricing);
      setLivePricingErrors(nextErrors);
    })();
    return () => {
      cancelled = true;
    };
  }, [visibleTiers, getSubscriptionPricing]);

  // Check if user can downgrade to a specific tier
  const canDowngradeTo = (tierId: SubscriptionTierType): boolean => {
    if (!access) return true;
    const tierConfig = TIER_CONFIG[tierId as keyof typeof TIER_CONFIG];
    if (!tierConfig) return true;
    // Can't downgrade if current employee count exceeds tier limit
    return access.number_of_active_employees <= tierConfig.maxEmployees;
  };

  // Get downgrade warning message
  const getDowngradeWarning = (tierId: SubscriptionTierType): string | null => {
    if (!access) return null;
    const tierConfig = TIER_CONFIG[tierId as keyof typeof TIER_CONFIG];
    if (!tierConfig) return null;
    if (access.number_of_active_employees > tierConfig.maxEmployees) {
      return `You have ${access.number_of_active_employees} active employees. This plan supports up to ${tierConfig.maxEmployees}. Please deactivate ${access.number_of_active_employees - tierConfig.maxEmployees} employee(s) first.`;
    }
    return null;
  };

  const handleCheckout = async (subscriptionType: SubscriptionTierType) => {
    // Runtime validation for required fields
    const salonId = businessInfo?.id;
    const email = currentUser?.email;
    const salonCreatedDt = businessInfo?.created_dt;

    if (!salonId || !email || !salonCreatedDt) {
      console.error("Missing required checkout data:", {
        hasSalonId: !!salonId,
        hasEmail: !!email,
        hasSalonCreatedDt: !!salonCreatedDt,
      });
      setMessage("Unable to proceed. Please refresh the page and try again.");
      setMessageType("error");
      return;
    }

    setIsLoading(true);
    setLoadingTier(subscriptionType);
    setMessage("");

    try {
      const checkoutRequest: GetCheckoutUrlRequest = {
        salonId,
        email,
        salonCreatedDt,
        subscriptionType,
      };

      const response: GetCheckoutUrlResponse =
        await getCheckoutUrl(checkoutRequest);

      if (response.url) {
        // Full page redirect to Stripe Checkout
        // Note: This differs from Stripe Connect onboarding which opens in new tab
        // because subscription checkout is a one-time flow that should replace the current page
        window.location.href = response.url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      setMessage("An error occurred during checkout. Please try again.");
      setMessageType("error");
      setIsLoading(false);
      setLoadingTier(null);
    }
  };

  // Don't render until we have the required context data
  // This prevents rendering issues while context is loading
  if (!businessInfo || !currentUser) {
    return null;
  }

  const renderFeature = (
    feature: { name: string; included: boolean; note?: string },
    index: number,
  ) => (
    <div key={index} className={styles.feature}>
      <span
        className={`${styles.featureIcon} ${
          feature.included ? styles.featureIncluded : styles.featureExcluded
        }`}
      >
        <IconCheck size={16} stroke={2.5} />
      </span>
      <span className={styles.featureText}>
        {feature.name}
        {feature.note && (
          <span className={styles.featureNote}> ({feature.note})</span>
        )}
      </span>
    </div>
  );

  const renderPricingCard = (tier: SubscriptionTier) => {
    const isHighlighted = tier.highlighted;
    const isLoadingThis = loadingTier === tier.id;
    const isCurrentPlan = access?.subscription_tier === tier.id;
    const canDowngrade = canDowngradeTo(tier.id);
    const downgradeWarning = getDowngradeWarning(tier.id);

    return (
      <div
        key={tier.id}
        className={`${styles.card} ${isHighlighted ? styles.cardHighlighted : ""} ${isCurrentPlan ? styles.cardCurrent : ""}`}
      >
        {isCurrentPlan && (
          <div className={styles.currentBadge}>Current Plan</div>
        )}
        {tier.badge && !isCurrentPlan && (
          <div className={styles.badge}>{tier.badge}</div>
        )}

        <Text className={styles.tierName}>{tier.name}</Text>

        <div className={styles.priceContainer}>
          {(() => {
            const live = livePricing[tier.id];
            if (tier.fetchLivePrice && live?.unit_amount != null) {
              const dollars = (live.unit_amount / 100).toFixed(
                live.unit_amount % 100 === 0 ? 0 : 2,
              );
              return (
                <>
                  <span className={styles.currencySymbol}>$</span>
                  <span
                    className={`${styles.price} ${isHighlighted ? styles.priceHighlighted : ""}`}
                  >
                    {dollars}
                  </span>
                  <span className={styles.pricePeriod}>
                    /{live.interval ?? "mo"}
                  </span>
                </>
              );
            }
            if (tier.fetchLivePrice && !livePricingErrors[tier.id]) {
              // Live Stripe price still loading — match the hardcoded-price
              // layout (currency + amount + period) with skeleton placeholders
              // so the card doesn't jump when the real numbers arrive.
              return (
                <>
                  <Skeleton height={20} width={12} radius="sm" />
                  <Skeleton height={48} width={90} radius="sm" />
                  <Skeleton height={16} width={28} radius="sm" />
                </>
              );
            }
            // No live price (either non-live tier, or live fetch failed) →
            // fall back to the hardcoded `tier.price` so the user always sees
            // a number before clicking Buy Now.
            return (
              <>
                <span className={styles.currencySymbol}>$</span>
                <span
                  className={`${styles.price} ${isHighlighted ? styles.priceHighlighted : ""}`}
                >
                  {tier.price}
                </span>
                <span className={styles.pricePeriod}>/mo</span>
              </>
            );
          })()}
        </div>

        <Text className={styles.employeeLimit}>{tier.employeeLimit}</Text>

        <div className={styles.featuresList}>
          {tier.features.map((feature, index) => renderFeature(feature, index))}
        </div>

        <Text className={styles.description}>{tier.description}</Text>

        {downgradeWarning && (
          <Stack gap="xs" mb="sm">
            <Alert color="orange" p="xs">
              <Text size="xs">{downgradeWarning}</Text>
            </Alert>
            <Button
              variant="subtle"
              color="teal"
              size="xs"
              leftSection={<IconUserEdit size={14} />}
              onClick={() => navigate("/admin/settings/staff")}
            >
              Fix employees active status
            </Button>
          </Stack>
        )}

        <Button
          fullWidth
          color={
            tier.id === "independent"
              ? "var(--brand-teal)"
              : tier.id === "business_essentials"
                ? "var(--brand-lime-green)"
                : "var(--brand-burnt-orange)"
          }
          onClick={() => handleCheckout(tier.id)}
          disabled={isLoading || isCurrentPlan || !canDowngrade}
          loading={isLoadingThis}
        >
          {isCurrentPlan
            ? "Current Plan"
            : !canDowngrade
              ? "Too Many Employees"
              : "Buy Now"}
        </Button>
      </div>
    );
  };

  // Render current plan summary card
  const renderCurrentPlanSummary = () => {
    if (!access || access.subscription_tier === "trial") return null;

    const employeeUsage =
      (access.number_of_active_employees / access.max_allowed_employees) * 100;

    return (
      <Card withBorder p="lg" mb="xl" radius="md">
        <Group justify="space-between" mb="md">
          <div>
            <Text size="sm" c="dimmed">
              Current Plan
            </Text>
            <Text size="xl" fw={600}>
              {getTierDisplayName()}
            </Text>
          </div>
          <Badge color="teal" size="lg" variant="light">
            Active
          </Badge>
        </Group>

        <div>
          <Group justify="space-between" mb="xs">
            <Group gap="xs">
              <IconUsers size={16} />
              <Text size="sm">Employee Usage</Text>
            </Group>
            <Text size="sm" fw={500}>
              {access.number_of_active_employees} /{" "}
              {access.max_allowed_employees}
            </Text>
          </Group>
          <Progress
            value={employeeUsage}
            color={employeeUsage >= 90 ? "orange" : "teal"}
            size="sm"
            radius="xl"
          />
          {access.employees_remaining <= 2 &&
            access.employees_remaining > 0 && (
              <Text size="xs" c="orange" mt="xs">
                Only {access.employees_remaining} employee slot
                {access.employees_remaining !== 1 ? "s" : ""} remaining
              </Text>
            )}
        </div>
      </Card>
    );
  };

  return (
    <Box className={styles.container}>
      <LoadingOverlay visible={isLoading} />

      <Container p={0} size="lg">
        <div className={styles.header}>
          <Text className={styles.title}>
            Pagoda Labs - Booking Platform Access
          </Text>
          <Text className={styles.subtitle}>
            Choose the plan that fits your business
          </Text>
        </div>

        {message && (
          <div className={styles.alertContainer}>
            <Alert
              icon={
                messageType === "success" ? (
                  <IconCheck size={16} />
                ) : (
                  <IconAlertCircle size={16} />
                )
              }
              title={
                messageType === "success"
                  ? "Success!"
                  : messageType === "error"
                    ? "Error"
                    : "Checkout Status"
              }
              color={
                messageType === "success"
                  ? "green"
                  : messageType === "error"
                    ? "red"
                    : "blue"
              }
            >
              {message}
            </Alert>
          </div>
        )}

        <Stack gap="lg">
          {renderCurrentPlanSummary()}

          <div
            className={`${styles.cardsContainer} ${
              visibleTiers.length === 1 ? styles.cardsContainerSingle : ""
            }`}
          >
            {visibleTiers.map((tier) => renderPricingCard(tier))}
          </div>

          {!isMobile && (
            <Text ta="center" size="sm" c="dimmed" mt="md">
              All plans include a {TIER_CONFIG.trial.trialDays}-day free trial.
              Cancel anytime.
            </Text>
          )}
        </Stack>
      </Container>
    </Box>
  );
}

export default SubscriptionPricing;
